import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Trophy, 
  Users, 
  Clock, 
  Target,
  AlertCircle,
  CheckCircle,
  Loader2,
  Star,
  Gift,
  Zap,
  ArrowRight,
  Play,
  Crown,
  TrendingUp,
  Phone,
  Mail,
  Share2,
  ExternalLink,
  ArrowLeft,
  Home,
  ChevronDown,
  ChevronUp,
  User,
  UserPlus,
  Send
} from 'lucide-react';

import { useAuth } from '@/hooks/useAuthState';
import { useToast } from '@/hooks/use-toast';
import { queryClient } from '@/lib/queryClient';
import { EnhancedLiveLeaderboard } from '@/components/challenges/EnhancedLiveLeaderboard';
import { ProgressTracker } from '@/components/challenges/ProgressTracker';
import { ShareModule } from '@/components/challenges/ShareModule';
import { CompactPrizeBadges } from '@/components/challenges/CompactPrizeBadges';
import { EnhancedGamificationSystem } from '@/components/challenges/EnhancedGamificationSystem';
import { useOptimizedChallengeData } from '@/hooks/useOptimizedChallengeData';
import { CountdownTimer } from '@/components/challenges/CountdownTimer';
import Navbar from '@/components/navbar';
import Footer from '@/components/footer';
// import ChatBubble from '@/components/ChatBubble';
import { MobileTopBar } from '@/components/mobile/MobileTopBar';
import BetfredChallengeSignup from '@/components/betfred-challenge-signup';
import { UsernameSelectionModal } from '@/components/UsernameSelectionModal';
import ReferFriend from '@/components/refer-friend';
import iPhone17LeftImage from '@assets/iPhone-17-Air-Mockup-1-_1758801571917.png';
import iPhone17RightImage from '@assets/iPhone-17-Air-Mockup-3_1758801571918.png';

interface Challenge {
  id: string;
  name: string;
  description?: string;
  type: 'acquisition' | 'participation' | 'growth' | 'marketing';
  status: 'draft' | 'live' | 'ended' | 'scheduled';
  startDateTime: string;
  endDateTime: string;
  targetValue: number;
  prizeDetails: {
    '1st'?: { item: string; value: number };
    '2nd'?: { item: string; value: number };
    '3rd'?: { item: string; value: number };
  };
  totalParticipants: number;
  leader?: {
    gamified_name: string;
    count: number;
    percentage: number;
  };
  prize1?: string;
  prize2?: string;
  prize3?: string;
  customRules?: string;
  leftImageUrl?: string;
  rightImageUrl?: string;
  reviewMilestones?: string;
}

interface UserProgress {
  verified_referrals: number;
  pending_referrals: number;
  rejected_referrals: number;
  current_rank: number;
  distance_to_leader: number;
  distance_to_goal: number;
  today_referrals: number;
  velocity: string;
  estimated_rank_change: string;
  percentile: number;
  gamified_name: string;
}

interface Referral {
  email: string;
  status: 'verified' | 'pending' | 'rejected';
  referred_date: string;
  verified_date?: string;
  expires_at?: string;
}

interface LeaderboardEntry {
  rank: number;
  gamified_name: string;
  verified_count: number;
  velocity?: string;
  last_referral?: string;
  trending: 'up' | 'down' | 'stable';
}

interface JoinResponse {
  success: boolean;
  referral_code: string;
  referral_link: string;
  share_links: {
    whatsapp: string;
    facebook: string;
    twitter: string;
  };
}

// Collapsible Rules Component
function CollapsibleRules({ targetValue, prize1, customRules, reviewMilestones, challengeId, forceExpanded = false }: { 
  targetValue: number; 
  prize1?: string;
  customRules?: string;
  reviewMilestones?: string;
  challengeId?: number | string;
  forceExpanded?: boolean;
}) {
  const [showRules, setShowRules] = useState(forceExpanded);

  return (
    <Card className="border-2 border-gray-200">
      <CardContent className="p-4">
        <Button
          variant="ghost"
          onClick={() => !forceExpanded && setShowRules(!showRules)}
          className={`w-full flex items-center justify-between p-0 h-auto hover:bg-transparent ${forceExpanded ? 'cursor-default' : ''}`}
          data-testid="button-toggle-rules"
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <div className="text-left">
              <div className="font-semibold text-gray-900" data-testid="text-rules-header">Challenge Rules</div>
              <div className="text-sm text-gray-600">Tap to view rules and requirements</div>
            </div>
          </div>
          {!forceExpanded && (
            showRules ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )
          )}
        </Button>

        {(showRules || forceExpanded) && (
          <div className="mt-4 pt-4 border-t border-gray-200" data-testid="section-rules">
            {customRules ? (
              <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                {customRules}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-green-800 mb-2">✅ How to Win:</h4>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• Be the first to reach {targetValue} verified referrals to win {prize1 || 'the top prize'}</li>
                    {Number(challengeId) !== 16 && Number(challengeId) !== 22 && <li>• 2nd and 3rd place determined when winner is declared (If Applicable)</li>}
                    {Number(challengeId) !== 16 && <li>• Only verified sign-ups count (email verification required)</li>}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-yellow-800 mb-2">⚠️ Important:</h4>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• Must be 18+ and South African resident</li>
                    <li>• Duplicate or fake accounts will be disqualified</li>
                    <li>• Maximum 3 referrals from same IP address</li>
                    <li>• All referrals subject to verification audit</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-blue-800 mb-2">🔒 Security & Privacy:</h4>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• For security and privacy reasons, only usernames are displayed on the leaderboard</li>
                    <li>• Usernames are automatically assigned when you sign up</li>
                    <li>• Your real name and personal information remain private</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-red-800 mb-2">🚫 Fraud Prevention:</h4>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• Automated fraud detection active</li>
                    {reviewMilestones && <li>• Manual review at {reviewMilestones} referrals</li>}
                    <li>• Suspicious activity will result in immediate disqualification</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Main component for authenticated users with challenges
function AuthenticatedChallengesPage({ 
  initialUser = null, 
  initialSession = null,
  preloadedChallenge = null
}: { 
  initialUser?: any; 
  initialSession?: any; 
  preloadedChallenge?: Challenge | null;
}) {
  const { challengeId } = useParams<{ challengeId: string }>();
  const [, setLocation] = useLocation();
  const { session: authSession, user: authUser } = useAuth();
  const { toast } = useToast();

  // Use initial values first, then auth hook values
  const session = authSession || initialSession;
  const user = authUser || initialUser;

  // Use preloaded challenge data instead of fetching again
  const challenge = preloadedChallenge;
  const [joinData, setJoinData] = useState<JoinResponse | null>(null);
  const [loading, setLoading] = useState(false); // Set to false since challenge is preloaded
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<Array<{
    id: number;
    message: string;
    type: 'success' | 'info';
  }>>([]);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [pendingJoinAfterUsername, setPendingJoinAfterUsername] = useState(false);
  const [showOptionalInviteModal, setShowOptionalInviteModal] = useState(false);
  const [invitePromptDismissed, setInvitePromptDismissed] = useState(false);
  const [autoInviteModalActive, setAutoInviteModalActive] = useState(false);
  const [selectedFriendId, setSelectedFriendId] = useState<number | null>(null);
  const [friendInvites, setFriendInvites] = useState([
    { id: 1, name: '', email: '', phone: '', sending: false, sent: false, phoneError: '' },
  ]);
  const challengeInvitePromptKey = user?.id ? `challenge_invite_prompt_${user.id}` : null;

  // Refs to track previous data for change detection
  const prevLeaderboard = useRef<LeaderboardEntry[]>([]);
  const prevUserProgress = useRef<UserProgress | null>(null);
  const prevTotalParticipants = useRef<number>(0);

  useEffect(() => {
    if (typeof window === "undefined" || !challengeInvitePromptKey) return;
    try {
      const stored = window.localStorage.getItem(challengeInvitePromptKey);
      setInvitePromptDismissed(stored === "hidden");
    } catch (error) {
      console.warn("[CHALLENGE] Failed to read invite prompt state:", error);
    }
  }, [challengeInvitePromptKey]);

  const handleInviteModalClose = useCallback(() => {
    if (autoInviteModalActive && typeof window !== "undefined" && challengeInvitePromptKey) {
      try {
        window.localStorage.setItem(challengeInvitePromptKey, "hidden");
      } catch (error) {
        console.warn("[CHALLENGE] Failed to persist invite prompt state:", error);
      }
      setInvitePromptDismissed(true);
    }
    setAutoInviteModalActive(false);
    setShowOptionalInviteModal(false);
    setFriendInvites([
      { id: 1, name: "", email: "", phone: "", sending: false, sent: false, phoneError: "" },
    ]);
  }, [autoInviteModalActive, challengeInvitePromptKey]);

  // Generate or get session ID for tracking referrals
  const getSessionId = useCallback(() => {
    let sessionId = sessionStorage.getItem('referral_session_id');
    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('referral_session_id', sessionId);
    }
    return sessionId;
  }, []);

  // Capture referral parameters from URL and store in server-side database
  const captureReferralFromURL = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const referralCode = urlParams.get('ref');
    const social = urlParams.get('social') || 'direct';

    if (referralCode && challenge?.id) {
      try {
        console.log(`[REFERRAL] Capturing referral from URL: ${referralCode} for challenge ${challenge.id} via ${social}`);

        // Get session ID for tracking
        const sessionId = getSessionId();

        // Store referral data server-side
        const response = await fetch('/api/referrals/store', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            challengeId: challenge.id,
            referralCode,
            socialSource: social,
            ipAddress: undefined, // Will be captured server-side
            userAgent: navigator.userAgent,
            sessionId,
            expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
          })
        });

        const result = await response.json();

        if (result.success) {
          console.log(`[REFERRAL] Successfully stored referral ${result.referralId} server-side`);

          // Store referral ID locally for potential later use
          sessionStorage.setItem('pending_referral_id', result.referralId.toString());

          // Show notification to user
          const socialPlatform = social === 'fb' ? 'Facebook' : social === 'wa' ? 'WhatsApp' : social === 'x' ? 'Twitter' : 'direct';
          showNotification(`🎉 Referral from ${socialPlatform} captured! Sign up to join the challenge.`, 'info');
        } else {
          console.warn(`[REFERRAL] Failed to store referral: ${result.message}`);
          // Show less specific error to user to avoid confusion
          showNotification(`Referral link processed. Sign up to join the challenge!`, 'info');
        }
      } catch (error) {
        console.error('[REFERRAL] Error storing referral server-side:', error);
        // Fall back gracefully - don't break the user experience
        showNotification(`Welcome! Sign up to join the challenge.`, 'info');
      }
    }
  }, [challenge, getSessionId]);

  // Pagination state for leaderboard
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Handlers for pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Use optimized challenge data hook instead of duplicate polling queries
  const {
    challenge: optimizedChallenge,
    leaderboard: currentLeaderboard,
    leaderboardMeta,
    userProgress,
    referrals: referralsData,
    totalParticipants: currentTotalParticipants,
    isInitialLoading: leaderboardLoading,
    isRefreshing: userProgressFetching,
    hasJoined,
    refreshAllData
  } = useOptimizedChallengeData({
    challengeId: challengeId || '',
    enableRealTime: true,
    page: currentPage,
    pageSize: pageSize
  });

  // Keep loading state for backward compatibility
  const userProgressLoading = leaderboardLoading;

  // Fetch unified referral data from API (same code used for competitions)
  const { data: referralData } = useQuery({
    queryKey: ["/api/v1/challenges/referral-code"],
    queryFn: async () => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch('/api/v1/challenges/referral-code', { headers });
      if (!response.ok) {
        throw new Error('Failed to fetch referral code');
      }
      return response.json();
    },
    enabled: !!user?.id && !!session?.access_token && !!challenge
  });

  // Fetch friends list
  const { data: friendsData } = useQuery({
    queryKey: ['/api/friends'],
    enabled: !!user?.id && !!session?.access_token
  });

  const friends = (friendsData as { friends: Array<{ id: number; friendName?: string; friendUsername?: string; friendEmail?: string; friendPhone?: string }> })?.friends || [];

  // Helper function to generate referral data using unified API
  const generateReferralData = useCallback(() => {
    if (!referralData?.referralCode || !challenge) return null;

    const referralCode = referralData.referralCode;

    // Dynamic domain for referral links (works with localhost, replit, production, etc.)
    const currentDomain = window.location.origin;

    // Use the server referral route format that stores pending referrals
    const baseReferralUrl = `${currentDomain}/ref/${challenge.id}/${referralCode}`;

    // Use OG tags route for Facebook sharing to ensure dynamic meta tags
    const ogTagsUrl = `${currentDomain}/og/challenges/${challenge.id}?ref=${referralCode}&social=fb`;

    const shareMessage = `🎉 Join me in the ${challenge.name} challenge on PrizeDrop! Use my referral code ${referralCode} to compete with me and climb the leaderboard! 🏆`;

    const shareLinks = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareMessage + '\n\n' + baseReferralUrl + '/wa')}`,
      facebook: `https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogTagsUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}&url=${encodeURIComponent(baseReferralUrl + '/x')}`
    };

    // For direct sharing/copying, use the WhatsApp format (most common)
    const referralUrl = baseReferralUrl + '/direct';

    return { referralCode, referralLink: referralUrl, shareLinks };
  }, [referralData, challenge]);

  // Data now comes from optimized hook - no need to extract

  // Determine if we're still loading critical user state
  const isLoadingUserState = user && session?.access_token && challenge && userProgressLoading && !userProgressFetching;

  // Show notification with auto-dismiss after 5 seconds and stacking capability
  const showNotification = useCallback((message: string, type: 'success' | 'info' = 'success') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, 5000);
  }, []);

  // Real-time change detection and gamification triggers
  useEffect(() => {
    if (!currentLeaderboard.length) return;

    const prevData = prevLeaderboard.current;
    const prevProgressData = prevUserProgress.current;
    const prevParticipants = prevTotalParticipants.current;

    // Skip first load
    if (prevData.length === 0 && prevParticipants === 0) {
      prevLeaderboard.current = currentLeaderboard;
      prevUserProgress.current = userProgress;
      prevTotalParticipants.current = currentTotalParticipants;
      return;
    }

    // Detect new participants joining
    if (currentTotalParticipants > prevParticipants) {
      const newParticipants = currentTotalParticipants - prevParticipants;
      showNotification(
        `🎉 ${newParticipants} new participant${newParticipants > 1 ? 's' : ''} joined the challenge!`,
        'info'
      );
    }

    // Detect user progress improvements (referral success)
    if (prevProgressData && userProgress && userProgress.verified_referrals > prevProgressData.verified_referrals) {
      const newReferrals = userProgress.verified_referrals - prevProgressData.verified_referrals;
      showNotification(
        `🚀 +${newReferrals} successful referral${newReferrals > 1 ? 's' : ''}! You're climbing the leaderboard!`,
        'success'
      );
    }

    // Detect milestone achievements
    if (prevProgressData && userProgress && challenge) {
      const quarterMilestone = Math.floor(challenge.targetValue * 0.25);
      const halfMilestone = Math.floor(challenge.targetValue * 0.5);
      const threeQuarterMilestone = Math.floor(challenge.targetValue * 0.75);

      // Quarter milestone
      if (userProgress.verified_referrals >= quarterMilestone && 
          prevProgressData.verified_referrals < quarterMilestone) {
        showNotification(
          `🏆 Milestone achieved! You've reached 25% of the target!`,
          'success'
        );
      }

      // Half milestone
      if (userProgress.verified_referrals >= halfMilestone && 
          prevProgressData.verified_referrals < halfMilestone) {
        showNotification(
          `👑 Amazing! You've reached 50% of the target - Halfway Hero!`,
          'success'
        );
      }

      // Three-quarter milestone
      if (userProgress.verified_referrals >= threeQuarterMilestone && 
          prevProgressData.verified_referrals < threeQuarterMilestone) {
        showNotification(
          `🔥 Outstanding! You've reached 75% of the target!`,
          'success'
        );
      }
    }

    // Detect leaderboard position changes
    if (prevData.length > 0 && userProgress) {
      const userCurrentRank = currentLeaderboard.find(entry => 
        entry.gamified_name === (user?.username || `User${user?.id}`)
      )?.rank;

      const userPrevRank = prevData.find(entry => 
        entry.gamified_name === (user?.username || `User${user?.id}`)
      )?.rank;

      if (userCurrentRank && userPrevRank && userCurrentRank < userPrevRank) {
        const improvement = userPrevRank - userCurrentRank;
        showNotification(
          `📈 You moved up ${improvement} position${improvement > 1 ? 's' : ''} to #${userCurrentRank}!`,
          'success'
        );
      }

      // Detect if user reached top 10
      if (userCurrentRank && userCurrentRank <= 10 && userPrevRank && userPrevRank > 10) {
        showNotification(
          `⭐ Rising Star! You've reached the top 10 at position #${userCurrentRank}!`,
          'success'
        );
      }

      // Detect if user reached top 3
      if (userCurrentRank && userCurrentRank <= 3 && userPrevRank && userPrevRank > 3) {
        showNotification(
          `🥇 Incredible! You've reached the top 3 at position #${userCurrentRank}!`,
          'success'
        );
      }
    }

    // Update previous data references
    prevLeaderboard.current = currentLeaderboard;
    prevUserProgress.current = userProgress;
    prevTotalParticipants.current = currentTotalParticipants;

  }, [currentLeaderboard, userProgress, currentTotalParticipants, challenge, user, showNotification]);

  // Check if user needs username setup - PRIORITY #1 (before invite modal)
  const checkUsernameSetup = useCallback(async () => {
    if (!user?.id || !session?.access_token) return false;

    // Only check username setup if user is already joined and no modals are showing
    if (!hasJoined || showOptionalInviteModal || showUsernameModal) return false;

    try {
      const response = await fetch('/api/v1/challenges/user/needs-username-setup', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.needsSetup) {
          console.log('[USERNAME SETUP] User needs username customization - showing username modal first');
          setShowUsernameModal(true);
          return true; // Indicates username modal is shown
        }
      }
      return false; // Indicates no username modal needed
    } catch (error) {
      console.error('[USERNAME SETUP] Error checking username setup:', error);
      return false;
    }
  }, [user, session, hasJoined, showOptionalInviteModal, showUsernameModal]);

  // Process pending referrals using server-side storage
  const processPendingReferral = useCallback(async () => {
    if (!user?.id || !challenge?.id) return;

    try {
      // Get session ID for tracking referrals
      const sessionId = sessionStorage.getItem('referral_session_id');

      // Extract referral code from URL as fallback
      const urlParams = new URLSearchParams(window.location.search);
      const fallbackReferralCode = urlParams.get('ref');

      // Prepare request body with fallback parameters
      const requestBody: any = {};

      if (sessionId) {
        requestBody.sessionId = sessionId;
        console.log(`[REFERRAL] Checking for pending referrals with session ${sessionId}`);
      }

      // Always include fallback parameters for robustness
      if (challenge?.id) {
        requestBody.challengeId = challenge.id;
      }

      if (fallbackReferralCode) {
        requestBody.referralCode = fallbackReferralCode;
        console.log(`[REFERRAL] Including fallback referral code: ${fallbackReferralCode}`);
      }

      // If we have neither sessionId nor referral code, skip processing
      if (!sessionId && !fallbackReferralCode) {
        console.log('[REFERRAL] No session ID or referral code found, no pending referrals to process');
        return;
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch('/api/process-referral', {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();

        if (result.processed) {
          console.log('[REFERRAL] Successfully processed referral:', result);

          // Clear the session storage after successful processing
          sessionStorage.removeItem('pending_referral_id');

          // Invalidate and refetch user progress and leaderboard data
          queryClient.invalidateQueries({ queryKey: ["/api/v1/challenges/progress"] });
          queryClient.invalidateQueries({ queryKey: ["/api/v1/challenges/leaderboard"] });

          // Show success notification to user
          const socialPlatform = result.social === 'fb' ? 'Facebook' : 
                                result.social === 'wa' ? 'WhatsApp' : 
                                result.social === 'x' ? 'Twitter' : 'Direct';
          showNotification(`🎉 Referral from ${socialPlatform} processed successfully!`, 'success');
        } else {
          console.log('[REFERRAL] No pending referral to process:', result.message);
        }
      } else {
        const error = await response.json();
        console.log('[REFERRAL] Error processing referral:', error.message);
      }
    } catch (error) {
      console.error('[REFERRAL] Error processing pending referral:', error);
    }
  }, [user, session, challenge, queryClient]);

  // Challenge data is now preloaded - no need to fetch again

  // Capture referral from URL when challenge is loaded
  useEffect(() => {
    if (challenge) {
      captureReferralFromURL();
    }
  }, [challenge, captureReferralFromURL]);

  // Process pending referrals when user becomes available
  useEffect(() => {
    if (user && session) {
      processPendingReferral();
      checkUsernameSetup();
    }
  }, [user, session, processPendingReferral, checkUsernameSetup]);

  useEffect(() => {
    if (!challenge?.id || !hasJoined) return;
    if (invitePromptDismissed) return;
    if (showUsernameModal) return;
    setAutoInviteModalActive(true);
    setShowOptionalInviteModal(true);
  }, [challenge?.id, hasJoined, invitePromptDismissed, showUsernameModal, challengeInvitePromptKey]);


  // Auto-join challenge for users redirected after authentication
  useEffect(() => {
    const handleAutoJoinChallenge = async () => {
      // Only proceed if all required conditions are met
      if (!user || !session?.access_token || !challenge || hasJoined === undefined) {
        return;
      }

      // Don't auto-join if user is already joined
      if (hasJoined) {
        return;
      }

      // Check for indicators that user was redirected here after authentication
      const urlParams = new URLSearchParams(window.location.search);
      const intent = urlParams.get('intent');
      const fromAuth = urlParams.get('from') === 'auth';
      const autoJoin = urlParams.get('autoJoin');

      // Check if user was redirected from authentication or has explicit intent to join
      const shouldAutoJoin = intent === 'join' || fromAuth || autoJoin === 'true' || 
        // Also check for stored challenge context that indicates auth redirect
        document.referrer.includes('/auth');

      if (!shouldAutoJoin) {
        return;
      }

      console.log(`[AUTO-JOIN] Attempting to auto-join challenge ${challenge.id} for user ${user.id}`);

      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        };

        const response = await fetch(`/api/v1/challenges/${challenge.id}/join`, {
          method: 'POST',
          headers
        });

        if (response.ok) {
          const data = await response.json();
          setJoinData(data);

          console.log('[AUTO-JOIN] Successfully joined challenge:', data);

          showNotification(
            `🎉 Welcome! You've been automatically entered into ${challenge.name}! Start sharing your referral link to compete!`,
            'success'
          );

          // Clean up URL parameters to avoid re-triggering
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('intent');
          newUrl.searchParams.delete('from');
          newUrl.searchParams.delete('autoJoin');
          window.history.replaceState({}, '', newUrl.toString());

          // Force refresh of all relevant queries to update UI state immediately
          queryClient.invalidateQueries({
            queryKey: ["/api/v1/challenges/progress", challenge.id, user.id]
          });
          queryClient.invalidateQueries({
            queryKey: ["/api/v1/challenges/leaderboard", challenge.id]
          });
          queryClient.invalidateQueries({
            queryKey: ["/api/v1/challenges/referrals", challenge.id, user.id]
          });
          queryClient.invalidateQueries({
            queryKey: ["/api/v1/challenges/referral-code"]
          });

          // Clear pending referral cookie to prevent repeated auto-join attempts
          document.cookie = 'pending_referral=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';

        } else {
          const errorData = await response.json();

          // If already joined, just clean up and continue silently
          if (errorData.error && (
            errorData.error.includes('Already joined') || 
            errorData.error.includes('already joined') ||
            errorData.error.includes('already participating')
          )) {
            console.log('[AUTO-JOIN] User already joined, cleaning up URL params');
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('intent');
            newUrl.searchParams.delete('from');
            newUrl.searchParams.delete('autoJoin');
            window.history.replaceState({}, '', newUrl.toString());
            return;
          }

          console.log('[AUTO-JOIN] Failed to join challenge:', errorData.error);
          showNotification(
            'Failed to join challenge automatically. You can join manually using the Join Challenge button.',
            'info'
          );
        }
      } catch (error: any) {
        console.error('[AUTO-JOIN] Error during auto-join:', error);
        showNotification(
          'Failed to join challenge automatically. You can join manually using the Join Challenge button.',
          'info'
        );
      }
    };

    // Only run auto-join after user data is fully loaded
    if (user && session && challenge && hasJoined !== undefined) {
      // Add a small delay to ensure all data is loaded
      const timer = setTimeout(handleAutoJoinChallenge, 100);
      return () => clearTimeout(timer);
    }
  }, [user, session, challenge, hasJoined, showNotification]);

  // Note: Manual fetch functions removed - now using real-time queries with useQuery

  // Handle sending friend invite with challenge-specific referral link
  const handleSendInvite = async (index: number) => {
    const friend = friendInvites[index];

    // Skip if already sent
    if (friend.sent) {
      return;
    }

    // Validate form fields
    if (!friend.name.trim()) {
      toast({
        title: "Error",
        description: "Please enter your friend's name",
        variant: "destructive",
      });
      return;
    }

    if (!friend.phone.trim()) {
      toast({
        title: "Error",
        description: "Please enter your friend's phone number",
        variant: "destructive",
      });
      return;
    }

    // Validate phone number format (minimum 10 digits)
    const phoneDigits = friend.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast({
        title: "Invalid Phone Number",
        description: "Phone number must be at least 10 digits long",
        variant: "destructive",
      });
      return;
    }

    // Set sending state for this specific friend
    setFriendInvites(prev => prev.map((f, i) => 
      i === index ? { ...f, sending: true } : f
    ));

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch('/api/friends/invite-prizedrop', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          friendName: friend.name.trim(),
          friendEmail: friend.email.trim() || undefined,
          friendPhone: friend.phone.trim(),
          challengeId: challenge?.id // Send challenge-specific referral link
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send invitation');
      }

      toast({
        title: "Invitation Sent!",
        description: `${friend.name} will receive an SMS invitation to join this challenge.`,
      });

      // Remove the successfully sent invite row to show a clean form
      setFriendInvites(prev => {
        const updated = prev.filter((_, i) => i !== index);
        // If all invites were removed, add a fresh empty one
        if (updated.length === 0) {
          return [{ id: 1, name: '', email: '', phone: '', sending: false, sent: false, phoneError: '' }];
        }
        return updated;
      });

      if (autoInviteModalActive) {
        toast({
          title: "Invite Sent! 🎉",
          description: "Thanks for inviting a friend to join the fun.",
          variant: "default",
          duration: 5000,
        });
        handleInviteModalClose();
      }

      // Refresh friends list
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send invitation",
        variant: "destructive",
      });
      setFriendInvites(prev => prev.map((f, i) => 
        i === index ? { ...f, sending: false } : f
      ));
    }
  };

  // Debounce timer for phone validation
  const phoneCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update friend invite field
  const updateFriendInvite = (index: number, field: 'name' | 'email' | 'phone', value: string) => {
    setFriendInvites(prev => prev.map((f, i) => {
      if (i !== index) return f;
      
      const updated = { ...f, [field]: value };
      
      // Initial validation for phone number
      if (field === 'phone') {
        const phoneDigits = value.replace(/\D/g, '');
        if (value && phoneDigits.length < 10) {
          updated.phoneError = 'Phone must be at least 10 digits';
        } else {
          updated.phoneError = '';
        }
      }
      
      return updated;
    }));

    // Debounced check if phone already exists in database
    if (field === 'phone' && value) {
      const phoneDigits = value.replace(/\D/g, '');
      
      // Clear previous timeout
      if (phoneCheckTimeoutRef.current) {
        clearTimeout(phoneCheckTimeoutRef.current);
      }
      
      // Only check if we have at least 10 digits
      if (phoneDigits.length >= 10) {
        phoneCheckTimeoutRef.current = setTimeout(async () => {
          try {
            const response = await fetch('/api/check-phone', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ phone: value }),
            });
            
            const data = await response.json();
            
            if (!data.available) {
              setFriendInvites(prev => prev.map((f, i) => 
                i === index ? { ...f, phoneError: data.message || 'Phone number already registered' } : f
              ));
            }
          } catch (error) {
            console.error('Error checking phone:', error);
          }
        }, 500); // Wait 500ms after user stops typing
      }
    }
  };

  const handleJoinChallenge = async () => {
    if (!challenge) return;

    // Prevent joining if challenge has ended or is scheduled
    if (challenge.status === 'ended') {
      showNotification('This challenge has ended and is no longer accepting new participants.', 'info');
      return;
    }

    if (challenge.status === 'scheduled') {
      showNotification('This challenge has not started yet. Please wait for the countdown to finish.', 'info');
      return;
    }

    // If user is not logged in, redirect to auth
    if (!session?.access_token) {
      // Capture referral code from URL to preserve it in auth redirect
      const urlParams = new URLSearchParams(window.location.search);
      const referralCode = urlParams.get('ref');

      // Build auth URL with challenge ID and referral code if present
      let authUrl = `/auth?intent=join&challengeId=${challenge.id}`;
      if (referralCode) {
        authUrl += `&ref=${encodeURIComponent(referralCode)}`;
      }

      console.log(`[CHALLENGE] Redirecting to auth with referral preservation: ${authUrl}`);
      setLocation(authUrl);
      return;
    }

    // If user is already joined, check if they need to customize username
    if (hasJoined) {
      console.log('[JOIN DEBUG] User already joined');

      // Check if user needs to customize username
      try {
        const headers: Record<string, string> = {
          'Authorization': `Bearer ${session.access_token}`
        };

        const checkResponse = await fetch(`/api/v1/challenges/user/needs-username-setup`, {
          method: 'GET',
          headers
        });

        if (checkResponse.ok) {
          const setupData = await checkResponse.json();

          // Show username modal if needed (hasCustomizedUsername is false and joined at least one challenge)
          if (setupData.needsSetup) {
            console.log('[CHALLENGE] User already joined but needs username customization - showing username modal');
            setShowUsernameModal(true);
            return; // Don't redirect yet, wait for username customization
          }
        }
      } catch (error) {
        console.error('[CHALLENGE] Error checking username setup:', error);
      }

      // Otherwise, just redirect to challenge page
      setLocation(`/challenges/${challenge.id}`);
      return;
    }

    // Proceed with joining challenge
    await performJoinChallenge();
  };

  const performJoinChallenge = async () => {
    if (!challenge || !session?.access_token) return;

    // Allow user to join first, then check ID verification
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      };

      const response = await fetch(`/api/v1/challenges/${challenge.id}/join`, {
        method: 'POST',
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setJoinData(data);

        // Force refresh of all relevant queries to update UI state immediately
        queryClient.invalidateQueries({
          queryKey: ["/api/v1/challenges/progress", challenge.id, user.id]
        });
        queryClient.invalidateQueries({
          queryKey: ["/api/v1/challenges/leaderboard", challenge.id]
        });
        queryClient.invalidateQueries({
          queryKey: ["/api/v1/challenges/referrals", challenge.id, user.id]
        });
        queryClient.invalidateQueries({
          queryKey: ["/api/v1/challenges/referral-code"]
        });

        // Clear pending referral cookie to prevent repeated attempts
        document.cookie = 'pending_referral=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';

        // Show success notification
        showNotification(`🎉 Successfully joined ${challenge?.name || 'the challenge'}!`, 'success');

        if (!invitePromptDismissed) {
          console.log('[CHALLENGE] Challenge joined - showing optional invite friends modal');
          setAutoInviteModalActive(true);
          setShowOptionalInviteModal(true);
        }

        // Check if user needs to customize username after joining
        try {
          const headers: Record<string, string> = {
            'Authorization': `Bearer ${session.access_token}`
          };

          const checkResponse = await fetch(`/api/v1/challenges/user/needs-username-setup`, {
            method: 'GET',
            headers
          });

          if (checkResponse.ok) {
            const setupData = await checkResponse.json();

            // Show username modal if needed (hasCustomizedUsername is false and joined at least one challenge)
            if (setupData.needsSetup) {
              console.log('[CHALLENGE] User needs username customization after join - showing username modal');
              setShowUsernameModal(true);
              return; // Don't redirect yet, wait for username customization
            }
          }
        } catch (error) {
          console.error('[CHALLENGE] Error checking username setup after join:', error);
        }

        // ID verification is now done during signup - no need to check here

        // Redirect immediately to challenge page to show participant view
        setLocation(`/challenges/${challenge.id}`);
      } else {
        const errorData = await response.json();
        console.log('[JOIN DEBUG] Error response:', errorData);


        // If user is already joined, redirect them to challenge page anyway
        if (errorData.error && (
          errorData.error.includes('Already joined') || 
          errorData.error.includes('already joined') ||
          errorData.error.includes('already participating')
        )) {
          console.log('[JOIN DEBUG] User already joined, redirecting to challenge page');
          setLocation(`/challenges/${challenge.id}`);
          return;
        }

        console.log('[JOIN DEBUG] Setting error:', errorData.error);
        setError(errorData.error || 'Failed to join challenge');
      }
    } catch (error) {
      console.error('Failed to join challenge:', error);
      setError('Failed to join challenge. Please try again.');
    }
  };



  const handleUsernameSet = async (newUsername: string) => {
    console.log('[CHALLENGE] Username set successfully:', newUsername);
    setShowUsernameModal(false);

    // Refresh user data to get updated username
    queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
    queryClient.invalidateQueries({ queryKey: ['/api/user/dashboard-optimized'] });

    // If we were waiting to join after username setup, proceed now
    if (pendingJoinAfterUsername) {
      setPendingJoinAfterUsername(false);
      await performJoinChallenge();
    }
  };

  const handleSubmitReferral = async (email: string) => {
    if (!challenge) return;

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch('/api/v1/referrals/submit', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          referredEmail: email,
          challengeId: parseInt(challenge.id)
        })
      });

      if (response.ok) {
        // Data will refresh automatically via real-time queries
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit referral');
      }
    } catch (error) {
      console.error('Failed to submit referral:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600">Loading challenges...</p>
        </div>
      </div>
    );
  }

  // Show loading while determining user's participation status
  if (isLoadingUserState) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="container mx-auto px-4 py-6 sm:py-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 sm:p-3 bg-white/20 rounded-lg">
                    <Trophy className="h-6 w-6 sm:h-8 sm:w-8" />
                  </div>
                  <div className="flex-1">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">{challenge?.name || 'Challenge'}</h1>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
            <p className="text-gray-600">Loading your challenge progress...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="text-center p-6">
            <Clock className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Coming Soon!</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => setLocation('/competitions')} className="bg-blue-500 hover:bg-blue-600">
              <Trophy className="h-4 w-4 mr-2" />
              View Competitions Instead
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="text-center p-6">
            <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">No Live Challenges</h2>
            <p className="text-gray-600 mb-4">There are no live challenges right now. New challenges are added regularly!</p>
            <Button onClick={() => setLocation('/competitions')} className="bg-blue-500 hover:bg-blue-600">
              <Trophy className="h-4 w-4 mr-2" />
              Browse Competitions
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show challenge details for authenticated users
  return (
    <div className="min-h-screen bg-gray-50">
      <MobileTopBar />
      {/* Stacked Notifications - Bottom Right Corner */}
      <div className="fixed bottom-4 right-4 z-[9999] space-y-3 max-w-sm">
        {notifications.map((notification, index) => (
          <div 
            key={notification.id}
            className="animate-in slide-in-from-right-2 fade-in duration-300"
            style={{ 
              transform: `translateY(-${index * 4}px)`,
              zIndex: 9999 - index 
            }}
          >
            <div className={`
              px-6 py-4 rounded-lg shadow-xl border backdrop-blur-sm
              ${notification.type === 'success' 
                ? 'bg-green-50/95 border-green-200 text-green-800' 
                : 'bg-blue-50/95 border-blue-200 text-blue-800'
              }
              hover:scale-105 transition-transform duration-200
            `}>
              <div className="flex items-center space-x-2">
                {notification.type === 'success' ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-blue-600" />
                )}
                <span className="font-medium text-sm">{notification.message}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Conditional Header - Navbar for logged-out users, Dashboard nav for logged-in */}
      {user ? (
        <div className="bg-white shadow-sm border-b hidden lg:block">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <Button 
                variant="ghost" 
                onClick={() => setLocation('/dashboard')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                data-testid="button-back-dashboard"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>


              {userProgress?.gamified_name && (
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg mx-4 flex-shrink-0">
                  <User className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span 
                    className="font-semibold text-blue-900 cursor-pointer hover:text-blue-700 transition-colors whitespace-nowrap"
                    onClick={() => setLocation('/profile')}
                    data-testid="text-username-clickable"
                    title="This is your username for the challenges"
                  >
                    {userProgress.gamified_name}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <Navbar />
      )}

      {/* Mobile-Optimized Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 py-6 sm:py-8">
          <div className="max-w-6xl mx-auto">
            {/* Mobile-First Header Layout */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 sm:p-3 bg-white/20 rounded-lg">
                  <Trophy className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">{challenge.name}</h1>
                  <p className="text-blue-100 mt-1 text-sm sm:text-base">
                    First to {challenge.targetValue} verified referrals wins!
                  </p>
                </div>
              </div>
              <div className="flex justify-center sm:justify-end">
                <div className="flex flex-col items-center gap-2">
                  <Badge className={`px-3 py-1 text-sm ${
                    challenge.status === 'live' ? 'bg-green-500 text-white' :
                    challenge.status === 'ended' ? 'bg-gray-500 text-white' :
                    challenge.status === 'scheduled' ? 'bg-orange-500 text-white' :
                    'bg-gray-500 text-white'
                  }`}>
                    {challenge.status === 'live' ? '🔴 LIVE' :
                     challenge.status === 'ended' ? '⏹️ ENDED' :
                     challenge.status === 'scheduled' ? '⏰ STARTING SOON' :
                     challenge.status.toUpperCase()}
                  </Badge>
                  {challenge.status === 'scheduled' && challenge.startDateTime && (
                    <CountdownTimer targetDate={challenge.startDateTime} />
                  )}
                </div>
              </div>
            </div>

            {/* Mobile-Optimized Challenge Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div className="text-center p-3 sm:p-4 bg-white/10 rounded-lg backdrop-blur-sm">
                <div className="text-xl sm:text-2xl font-bold">{userProgress?.verified_referrals || 0}</div>
                <div className="text-xs sm:text-sm text-blue-100">Referrals</div>
              </div>
              <div className="text-center p-3 sm:p-4 bg-white/10 rounded-lg backdrop-blur-sm">
                <div className="text-xl sm:text-2xl font-bold">#{userProgress?.current_rank || '-'}</div>
                <div className="text-xs sm:text-sm text-blue-100">Rank</div>
              </div>
              <div className="text-center p-3 sm:p-4 bg-white/10 rounded-lg backdrop-blur-sm">
                <div className="text-xl sm:text-2xl font-bold">{challenge.targetValue}</div>
                <div className="text-xs sm:text-sm text-blue-100">Target</div>
              </div>
              <div className="text-center p-3 sm:p-4 bg-white/10 rounded-lg backdrop-blur-sm">
                <div className="text-lg sm:text-2xl font-bold">
                  {userProgress?.verified_referrals || 0}/{challenge.targetValue}
                </div>
                <div className="text-xs sm:text-sm text-blue-100">Progress</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons Section - After Hero */}
      {hasJoined && user?.id && (
        <div className="bg-white border-b border-gray-200">
          <div className="container mx-auto px-4 py-4">
            <div className="max-w-6xl mx-auto space-y-3">
              {/* Primary Action Buttons - Responsive Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Invite Friends Button - PRIMARY CALL TO ACTION */}
                <Button
                  onClick={() => {
                    setAutoInviteModalActive(false);
                    setShowOptionalInviteModal(true); // Show optional dismissible modal
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 text-lg shadow-lg"
                  data-testid="button-invite-friends"
                >
                  <UserPlus className="h-5 w-5 mr-2" />
                  Invite your friends
                </Button>

                {/* Share Referral Link Button - SECONDARY ACTION */}
                {(() => {
                  const referralData = generateReferralData();

                  return referralData ? (
                    <ShareModule
                      challengeId={Number(challenge.id)}
                      challengeName={challenge.name}
                      referralCode={referralData.referralCode}
                      referralLink={referralData.referralLink}
                      shareLinks={referralData.shareLinks}
                      hideBetfredButton={true}
                    />
                  ) : null;
                })()}
              </div>

              {/* Betfred Button - Full Width on Desktop */}
              {(() => {
                const referralData = generateReferralData();

                return referralData ? (
                  <ShareModule
                    challengeId={Number(challenge.id)}
                    challengeName={challenge.name}
                    referralCode={referralData.referralCode}
                    referralLink={referralData.referralLink}
                    shareLinks={referralData.shareLinks}
                    showOnlyBetfredButton={true}
                  />
                ) : null;
              })()}

              {/* Betfred Challenge Signup */}
              <BetfredChallengeSignup challengeId={parseInt(challenge.id)} title={challenge.name} />
            </div>
          </div>
        </div>
      )}

      {/* OPTIONAL Invite Modal - Dismissible, shown when user clicks "Invite your friends" button */}
      <Dialog 
        open={showOptionalInviteModal}
        onOpenChange={(open) => {
          if (open) {
            setShowOptionalInviteModal(true);
          } else {
            handleInviteModalClose();
          }
        }}
      >
        <DialogContent className="w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] md:max-w-md lg:max-w-xl xl:max-w-2xl max-h-[90vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle>Invite more friends &amp; climb the leaderboard</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {/* Header row - Hidden on mobile */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-2 text-sm font-medium text-gray-700">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Email (Optional)</div>
              <div className="col-span-3">Phone Number</div>
              <div className="col-span-3"></div>
            </div>

            {/* Friend invite rows */}
            {friendInvites.map((friend, index) => (
              <div key={friend.id} className="space-y-3 p-3 md:p-4 border md:border-0 border-gray-200 rounded-lg md:rounded-none bg-gray-50 md:bg-transparent">
                {/* Mobile Layout */}
                <div className="block md:hidden space-y-2">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600">Name</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="John Doe"
                      value={friend.name}
                      onChange={(e) => updateFriendInvite(index, 'name', e.target.value)}
                      data-testid={`input-friend-name-${index}`}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600">Phone</label>
                    <input
                      type="tel"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        friend.phoneError 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="27123456789"
                      value={friend.phone}
                      onChange={(e) => updateFriendInvite(index, 'phone', e.target.value)}
                      data-testid={`input-friend-phone-${index}`}
                    />
                    {friend.phoneError && (
                      <p className="text-xs text-red-600 mt-1">{friend.phoneError}</p>
                    )}
                  </div>

                  <Button
                    className="w-full h-10"
                    onClick={() => handleSendInvite(index)}
                    disabled={friend.sending || friend.sent || !friend.name.trim() || !friend.phone.trim() || !!friend.phoneError}
                    data-testid={`button-send-invite-${index}`}
                  >
                    {friend.sending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : friend.sent ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Sent
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send
                      </>
                    )}
                  </Button>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:grid grid-cols-12 gap-2 items-start">
                  <input
                    type="text"
                    className="col-span-3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="John Doe"
                    value={friend.name}
                    onChange={(e) => updateFriendInvite(index, 'name', e.target.value)}
                    data-testid={`input-friend-name-${index}`}
                  />

                <input
                  type="email"
                  className="col-span-3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="friend@example.com"
                  value={friend.email}
                  onChange={(e) => updateFriendInvite(index, 'email', e.target.value)}
                  data-testid={`input-friend-email-${index}`}
                />

                <div className="col-span-3">
                  <input
                    type="tel"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      friend.phoneError 
                        ? 'border-red-500 focus:ring-red-500' 
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="27123456789"
                    value={friend.phone}
                    onChange={(e) => updateFriendInvite(index, 'phone', e.target.value)}
                    data-testid={`input-friend-phone-${index}`}
                  />
                  {friend.phoneError && (
                    <p className="text-xs text-red-600 mt-1">{friend.phoneError}</p>
                  )}
                </div>

                <Button
                  className="col-span-3"
                  onClick={() => handleSendInvite(index)}
                  disabled={friend.sending || friend.sent || !friend.name.trim() || !friend.phone.trim() || !!friend.phoneError}
                  data-testid={`button-send-invite-${index}`}
                >
                  {friend.sending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : friend.sent ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Invite Sent
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Invite
                    </>
                  )}
                </Button>
                </div>
              </div>
            ))}

          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleInviteModalClose}
              data-testid="button-close-invite-modal"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile-Optimized Main Content */}
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="max-w-6xl mx-auto">
          {!hasJoined ? (
            /* Join Challenge Section */
            <div className="space-y-6">
              <Alert className="border-blue-200 bg-blue-50">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  Join this challenge to start competing and track your progress!
                </AlertDescription>
              </Alert>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-yellow-500" />
                    {challenge.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Challenge Details</h3>
                      <p className="text-gray-600">
                        Compete to be the first to reach {challenge.targetValue} verified referrals and win amazing prizes!
                      </p>
                    </div>

                    {/* Compact Prize Badges */}
                    <div className="text-center">
                      <CompactPrizeBadges prizeDetails={challenge.prizeDetails} className="mb-4" />
                    </div>

                    {/* Competition Rules - Available to all users */}
                    <CollapsibleRules 
                      targetValue={challenge.targetValue} 
                      prize1={challenge.prize1}
                      customRules={challenge.customRules}
                      challengeId={challenge.id}
                      reviewMilestones={challenge.reviewMilestones}
                    />

                    <div className="text-center pt-4">
                      <Button 
                        onClick={handleJoinChallenge}
                        size="lg"
                        className={challenge.status === 'ended' || challenge.status === 'scheduled'
                          ? "bg-gray-400 text-gray-200 cursor-not-allowed px-8 py-4 text-lg font-semibold"
                          : "bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 px-8 py-4 text-lg font-semibold"
                        }
                        disabled={challenge.status === 'ended' || challenge.status === 'scheduled'}
                        data-testid="button-join-challenge"
                      >
                        {challenge.status === 'ended' ? (
                          <>Challenge Ended</>
                        ) : challenge.status === 'scheduled' ? (
                          <>Starting Soon</>
                        ) : (
                          <>
                            <Trophy className="h-5 w-5 mr-2" />
                            Join Challenge Now
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Live Leaderboard for non-participants */}
              <EnhancedLiveLeaderboard
                challengeId={challenge.id}
                entries={currentLeaderboard}
                totalParticipants={challenge.totalParticipants}
                isLive={true}
                pagination={leaderboardMeta?.pagination}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                loading={leaderboardLoading}
              />
            </div>
          ) : (
            /* Mobile-First Participant Dashboard - Single Column Layout */
            <div className="space-y-4 sm:space-y-6">
              {/* Compact Prize Display */}
              <div className="text-center py-4 px-6 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Trophy className="h-5 w-5 text-yellow-600" />
                  <span className="font-semibold text-yellow-800">Challenge Prizes</span>
                </div>
                <CompactPrizeBadges prizeDetails={challenge.prizeDetails} className="mb-3" />
                <p className="text-sm text-gray-600">
                  Be the first to reach <span className="font-bold text-yellow-800">{challenge.targetValue} verified referrals</span> to win!
                </p>
              </div>


              {/* Mini Leaderboard - Top 3 spots, expandable */}
              <EnhancedLiveLeaderboard
                challengeId={challenge.id}
                entries={currentLeaderboard}
                totalParticipants={challenge.totalParticipants}
                userRank={userProgress?.current_rank}
                userVerifiedCount={userProgress?.verified_referrals}
                userGamifiedName={userProgress?.gamified_name}
                isLive={true}
                pagination={leaderboardMeta?.pagination}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                loading={leaderboardLoading}
              />

              {/* Rules - Collapsible section */}
              <CollapsibleRules 
                targetValue={challenge.targetValue}
                prize1={challenge.prize1}
                customRules={challenge.customRules}
                challengeId={challenge.id}
                reviewMilestones={challenge.reviewMilestones}
              />

              {/* Progress Tracker - Always available for joined users */}
              {userProgress && (
                <ProgressTracker
                  challengeId={challenge.id}
                  challengeName={challenge.name}
                  targetValue={challenge.targetValue}
                  progress={userProgress}
                  referrals={referralsData || []}
                  onSubmitReferral={handleSubmitReferral}
                />
              )}

              {/* Achievements Section - Hidden as requested by user */}
            </div>
          )}
        </div>
      </div>

      {/* Conditional Footer - Only for logged-out users */}
      {!user && <Footer />}

      {/* Username Selection Modal */}
      <UsernameSelectionModal
        isOpen={showUsernameModal}
        currentUsername={user?.username || ''}
        onUsernameSet={handleUsernameSet}
        requireUsername={hasJoined} // Require username if user has already joined
        onClose={hasJoined ? undefined : () => {
          setShowUsernameModal(false);
          setPendingJoinAfterUsername(false);
        }}
      />
    </div>
  );
}

// Import iPhone mockup images

// Public Challenge Overview Component - Accessible to all users
function PublicChallengeOverview({ challenge, loading, error }: {
  challenge: Challenge | null;
  loading: boolean;
  error: string | null;
}) {
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600">Loading challenge...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <Alert className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!challenge) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-8 relative">
            {/* Challenge Hero Images - Positioned around hero section only */}
            {challenge.leftImageUrl && (
              <div className="hidden xl:block absolute left-[-200px] top-1/2 transform -translate-y-1/2 z-10">
                <img 
                  src={challenge.leftImageUrl} 
                  alt="Challenge Image" 
                  className="w-48 h-auto opacity-80 hover:opacity-100 transition-opacity duration-300"
                  data-testid="img-challenge-left"
                />
              </div>
            )}
            {challenge.rightImageUrl && (
              <div className="hidden xl:block absolute right-[-200px] top-1/2 transform -translate-y-1/2 z-10">
                <img 
                  src={challenge.rightImageUrl} 
                  alt="Challenge Image" 
                  className="w-48 h-auto opacity-80 hover:opacity-100 transition-opacity duration-300"
                  data-testid="img-challenge-right"
                />
              </div>
            )}
            <div className="flex items-center justify-center gap-3 mb-4">
              <Trophy className="h-8 w-8 text-yellow-500" />
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                {challenge.name}
              </h1>
            </div>
            <p className="text-lg text-gray-600 mb-6">
              Compete to be the first to reach <span className="font-bold text-blue-600">{challenge.targetValue} verified referrals</span> and win amazing prizes!
            </p>

            {/* Join Challenge Button for Non-Authenticated Users - Moved to top */}
            {!user && (
              <div className="text-center mb-8">
                <Button
                  onClick={() => {
                    // Prevent action if challenge has ended or is scheduled
                    if (challenge.status === 'ended' || challenge.status === 'scheduled') {
                      return;
                    }

                    // Capture referral code from URL to preserve it in auth redirect
                    const urlParams = new URLSearchParams(window.location.search);
                    const referralCode = urlParams.get('ref');

                    // Build auth URL with challenge ID and referral code if present
                    let authUrl = `/auth?intent=join&challengeId=${challenge.id}`;
                    if (referralCode) {
                      authUrl += `&ref=${encodeURIComponent(referralCode)}`;
                    }

                    console.log(`[CHALLENGE] Redirecting to auth with referral preservation: ${authUrl}`);
                    setLocation(authUrl);
                  }}
                  size="lg"
                  className={challenge.status === 'ended' || challenge.status === 'scheduled'
                    ? "bg-gray-400 text-gray-200 cursor-not-allowed px-8 py-4 text-lg font-semibold"
                    : "bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 px-8 py-4 text-lg font-semibold transform hover:scale-[1.02] transition-all duration-200 shadow-md hover:shadow-lg animate-[shake_0.3s_ease-in-out_5s_infinite]"
                  }
                  disabled={challenge.status === 'ended' || challenge.status === 'scheduled'}
                  data-testid="button-join-challenge-public"
                >
                  {challenge.status === 'ended' ? (
                    <>Challenge Ended</>
                  ) : challenge.status === 'scheduled' ? (
                    <>Starting Soon</>
                  ) : (
                    <>
                      <Trophy className="h-5 w-5 mr-2" />
                      Join Challenge Now
                    </>
                  )}
                </Button>
                <p className="text-gray-600 mt-4">
                  {challenge.status === 'ended' 
                    ? 'This challenge has ended and is no longer accepting participants.'
                    : challenge.status === 'scheduled'
                    ? 'This challenge has not started yet. Please wait for it to begin.'
                    : 'Join thousands of participants competing for amazing prizes!'
                  }
                </p>
              </div>
            )}

            {/* Prize Display */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/50 mb-8">
              <CompactPrizeBadges prizeDetails={challenge.prizeDetails} className="mb-4" />
              <div className="flex items-center justify-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>{challenge.totalParticipants} participants</span>
                </div>
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  <span>Target: {challenge.targetValue} referrals</span>
                </div>
              </div>
            </div>
          </div>


          {/* Challenge Rules - Always Visible, expanded for logged-out users */}
          <div className="mb-8">
            <CollapsibleRules 
              targetValue={challenge.targetValue}
              prize1={challenge.prize1}
              customRules={challenge.customRules}
              challengeId={challenge.id}
              reviewMilestones={challenge.reviewMilestones}
              forceExpanded={!user} 
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ChallengePage() {
  const { user, loading: authLoading, session } = useAuth();
  const { toast } = useToast();
  const { challengeId } = useParams<{ challengeId: string }>();
  const [, setLocation] = useLocation();

  // Challenge data state - shared between public and authenticated views
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [challengeLoading, setChallengeLoading] = useState(true);
  const [challengeError, setChallengeError] = useState<string | null>(null);

  // Fetch challenge data - accessible to all users
  const fetchChallenge = async () => {
    if (!challengeId) {
      setChallengeError('No challenge ID provided');
      setChallengeLoading(false);
      return;
    }

    try {
      setChallengeLoading(true);
      setChallengeError(null);

      // Get specific challenge by ID, or fallback to active race
      let response;
      let data;

      // Try to get specific challenge first
      try {
        response = await fetch(`/api/v1/challenges/${challengeId}`);
        if (response.ok) {
          data = await response.json();
        } else {
          throw new Error('Challenge not found');
        }
      } catch {
        // Fallback to active race if specific challenge not found
        response = await fetch('/api/v1/challenges/active-race');
        data = await response.json();
      }

      if (data.challenge || data.id) {
        // Handle both direct challenge response and active-race response formats
        const challengeInfo = data.challenge || data;
        // Transform prizes from API format to expected format
        const transformPrizes = (prizes: any) => {
          const prizeDetails: Challenge['prizeDetails'] = {};
          if (prizes?.prize1) {
            try {
              const prize = typeof prizes.prize1 === 'string' ? JSON.parse(prizes.prize1) : prizes.prize1;
              prizeDetails['1st'] = { item: prize.item || 'Prize 1', value: prize.value || 0 };
            } catch (e) {
              prizeDetails['1st'] = { item: prizes.prize1 || 'Prize 1', value: 0 };
            }
          }
          if (prizes?.prize2) {
            try {
              const prize = typeof prizes.prize2 === 'string' ? JSON.parse(prizes.prize2) : prizes.prize2;
              prizeDetails['2nd'] = { item: prize.item || 'Prize 2', value: prize.value || 0 };
            } catch (e) {
              prizeDetails['2nd'] = { item: prizes.prize2 || 'Prize 2', value: 0 };
            }
          }
          if (prizes?.prize3) {
            try {
              const prize = typeof prizes.prize3 === 'string' ? JSON.parse(prizes.prize3) : prizes.prize3;
              prizeDetails['3rd'] = { item: prize.item || 'Prize 3', value: prize.value || 0 };
            } catch (e) {
              prizeDetails['3rd'] = { item: prizes.prize3 || 'Prize 3', value: 0 };
            }
          }
          return prizeDetails;
        };

        const challengeData: Challenge = {
          id: challengeInfo.id.toString(),
          name: challengeInfo.name,
          type: 'acquisition',
          status: challengeInfo.status || 'live',
          startDateTime: challengeInfo.startDateTime || new Date().toISOString(),
          endDateTime: challengeInfo.endDateTime || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          targetValue: challengeInfo.target || challengeInfo.targetValue,
          prizeDetails: challengeInfo.prizeDetails || transformPrizes(challengeInfo.prizes),
          totalParticipants: challengeInfo.total_participants || challengeInfo.totalParticipants || 0,
          leader: challengeInfo.leader
        };

        setChallenge(challengeData);

      } else {
        setChallengeError('Challenge not found. Please check the challenge ID or try again later.');
      }
    } catch (error) {
      console.error('Failed to fetch challenge:', error);
      setChallengeError('Failed to load challenge data. Please try again later.');
    } finally {
      setChallengeLoading(false);
    }
  };

  // Fetch challenge data on mount
  useEffect(() => {
    fetchChallenge();
  }, [challengeId]);

  // Show loading while auth is being determined
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation - Only for logged-out users */}
      {!user && <Navbar />}

      {/* Public Challenge Overview - Only for logged-out users */}
      {!user && (
        <PublicChallengeOverview 
          challenge={challenge}
          loading={challengeLoading}
          error={challengeError}
        />
      )}

      {/* Authenticated Features - Only for logged-in users with valid challenge data */}
      {user && challenge && (
        <AuthenticatedChallengesPage 
          initialUser={user} 
          initialSession={session}
          preloadedChallenge={challenge}
        />
      )}

      {/* Footer for logged-out users */}
      {!user && <Footer />}

      {/* Chat Support Bubble */}
      {/* <ChatBubble /> */}
    </div>
  );
}

export default ChallengePage;
